import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { PageHeader } from "../../shared/ui/PageHeader";
import {
  PatientCombobox,
  type PatientChoice,
} from "../appointments/components/PatientCombobox";
import {
  vaccinationApi,
  vaxBookingStatusLabel,
  type VaxBooking,
} from "../../shared/api/vaccination";
import { formatDate } from "../../shared/format";
import { PatientLink } from "../../shared/ui/PatientLink";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

function todayIso(): string {
  return new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Ho_Chi_Minh" });
}

type BadgeVariant = NonNullable<BadgeProps["variant"]>;

const STATUS_VARIANT: Record<string, BadgeVariant> = {
  REQUESTED: "warning",
  APPROVED: "secondary",
  SCHEDULED: "secondary",
  FULFILLED: "success",
  CANCELLED: "muted",
};

/**
 * Đặt lịch tiêm chủng (staff-side). A vaccination booking needs only a patient,
 * a vaccine/protocol and a preferred day — no doctor, no time slot — so it is a
 * `vaccine_request`, deliberately separate from the doctor+slot exam appointment.
 */
export function VaccinationBookingPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [day, setDay] = useState(todayIso());
  const [q, setQ] = useState("");

  // create form
  const [patient, setPatient] = useState<PatientChoice | null>(null);
  const [protocolIds, setProtocolIds] = useState<string[]>([]);
  const [preferredDate, setPreferredDate] = useState(todayIso());
  const [note, setNote] = useState("");
  const [formErr, setFormErr] = useState<string | null>(null);

  const protocols = useQuery({
    queryKey: ["vaccination", "protocols"],
    queryFn: () => vaccinationApi.listProtocols(),
    staleTime: 5 * 60 * 1000,
  });

  const bookings = useQuery({
    queryKey: ["vaccination", "bookings", day, q],
    queryFn: () => vaccinationApi.listBookings({ date: day, q: q.trim() || undefined }),
  });

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["vaccination", "bookings"] });

  const create = useMutation({
    // One booking per selected vaccine — a patient can be booked for several
    // vaccines on the same day (they get one screening, multiple jabs).
    mutationFn: () =>
      Promise.all(
        protocolIds.map((pid) =>
          vaccinationApi.createBooking({
            patientId: patient!.id,
            protocolId: pid,
            preferredDate: preferredDate || null,
            note: note.trim() || null,
          })
        )
      ),
    onSuccess: () => {
      setPatient(null);
      setProtocolIds([]);
      setNote("");
      setFormErr(null);
      if (preferredDate) setDay(preferredDate);
      invalidate();
    },
    onError: (e: unknown) =>
      setFormErr(e instanceof Error ? e.message : "Không đặt được lịch."),
  });

  function toggleProtocol(id: string) {
    setProtocolIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
  }

  const confirm = useMutation({
    mutationFn: (id: string) => vaccinationApi.confirmBooking(id),
    onSuccess: invalidate,
  });
  const cancel = useMutation({
    mutationFn: (id: string) => vaccinationApi.cancelBooking(id),
    onSuccess: invalidate,
  });
  const fulfill = useMutation({
    mutationFn: (id: string) => vaccinationApi.fulfillBooking(id),
    onSuccess: invalidate,
  });

  const canSubmit = !!patient && protocolIds.length > 0 && !create.isPending;

  const rows = bookings.data ?? [];
  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    rows.forEach((r) => (c[r.status] = (c[r.status] ?? 0) + 1));
    return c;
  }, [rows]);

  function startVaccination(b: VaxBooking) {
    // Hand off to the screening/administer workspace with the patient (and vaccine)
    // preselected; the booking stays SCHEDULED until the nurse marks it "Đã tiêm".
    const proto = b.protocolId ? `&protocolId=${b.protocolId}` : "";
    navigate(`/vaccination/workspace?patientId=${b.patientId}${proto}`);
  }

  return (
    <div>
      <PageHeader
        title="Đặt lịch tiêm chủng"
        description="Đặt lịch tiêm chỉ cần bệnh nhân + vắc xin + ngày — không cần chọn bác sĩ hay giờ."
      />

      {/* Create form */}
      <Card className="mb-4">
        <CardContent className="p-4">
          <h3 className="mb-3 text-base font-semibold text-foreground">Đặt lịch mới</h3>
          <div className="space-y-3">
            {/* Row 1 — the two tall pickers (top-aligned) */}
            <div className="grid grid-cols-1 items-start gap-3 md:grid-cols-2">
              <PatientCombobox value={patient} onChange={setPatient} />
              <div className="grid gap-1.5">
                <Label>
                  Vắc xin / Phác đồ{" "}
                  {protocolIds.length > 0 && (
                    <span className="font-normal text-muted-foreground">
                      · đã chọn {protocolIds.length}
                    </span>
                  )}
                </Label>
                <div className="max-h-[132px] overflow-y-auto rounded-md border border-input px-2 py-1">
                  {(protocols.data ?? []).map((p) => (
                    <label
                      key={p.id}
                      className="flex cursor-pointer items-center gap-2 py-1 text-sm"
                    >
                      <Checkbox
                        checked={protocolIds.includes(p.id)}
                        onCheckedChange={() => toggleProtocol(p.id)}
                      />
                      {p.name}
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Row 2 — day + note + submit (bottom-aligned) */}
            <div className="grid grid-cols-1 items-end gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]">
              <div className="grid gap-1.5">
                <Label htmlFor="vax-preferred-date">Ngày mong muốn</Label>
                <Input
                  id="vax-preferred-date"
                  type="date"
                  value={preferredDate}
                  onChange={(e) => setPreferredDate(e.target.value)}
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="vax-note">Ghi chú</Label>
                <Input
                  id="vax-note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Tuỳ chọn"
                />
              </div>
              <Button
                type="button"
                className="sm:w-auto"
                disabled={!canSubmit}
                onClick={() => create.mutate()}
              >
                {create.isPending ? "Đang đặt…" : "Đặt lịch"}
              </Button>
            </div>
          </div>
          {formErr && <p className="mt-2 text-sm text-destructive">{formErr}</p>}
        </CardContent>
      </Card>

      {/* Filters */}
      <div className="mb-3 flex flex-wrap items-end gap-3">
        <div className="grid gap-1.5">
          <Label htmlFor="vax-filter-day">Ngày</Label>
          <Input
            id="vax-filter-day"
            type="date"
            value={day}
            onChange={(e) => setDay(e.target.value)}
            className="w-auto"
          />
        </div>
        <div className="grid min-w-[200px] flex-1 gap-1.5">
          <Label htmlFor="vax-filter-q">Tìm bệnh nhân</Label>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              id="vax-filter-q"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Tên / SĐT / mã BN"
              className="pl-9"
            />
          </div>
        </div>
        <div className="text-sm text-muted-foreground">
          {rows.length} lịch · Đã xếp {counts.SCHEDULED ?? 0} · Chờ {counts.REQUESTED ?? 0} · Đã tiêm{" "}
          {counts.FULFILLED ?? 0}
        </div>
      </div>

      {/* List */}
      <div className="rounded-md border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Mã đặt lịch</TableHead>
              <TableHead>Bệnh nhân</TableHead>
              <TableHead>Vắc xin</TableHead>
              <TableHead>Ngày</TableHead>
              <TableHead>Trạng thái</TableHead>
              <TableHead className="text-right">Thao tác</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {bookings.isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                  Đang tải…
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                  Chưa có lịch tiêm cho ngày này.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((b) => {
                const terminal = b.status === "FULFILLED" || b.status === "CANCELLED";
                return (
                  <TableRow key={b.id}>
                    <TableCell className="whitespace-nowrap font-mono text-sm font-medium">
                      {b.bookingCode}
                    </TableCell>
                    <TableCell>
                      <PatientLink id={b.patientId} name={b.patientName} className="font-medium" />
                      <div className="text-xs text-muted-foreground">
                        {b.patientCode || "—"}
                        {b.patientPhone ? ` · ${b.patientPhone}` : ""}
                      </div>
                    </TableCell>
                    <TableCell>{b.vaccineName || b.protocolName || "—"}</TableCell>
                    <TableCell>{b.preferredDate ? formatDate(b.preferredDate) : "—"}</TableCell>
                    <TableCell>
                      <Badge variant={STATUS_VARIANT[b.status] ?? "outline"}>
                        {vaxBookingStatusLabel[b.status] ?? b.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-right">
                      <div className="flex justify-end gap-2">
                        {b.status === "REQUESTED" && (
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            onClick={() => confirm.mutate(b.id)}
                          >
                            Xác nhận
                          </Button>
                        )}
                        {b.status === "SCHEDULED" && (
                          <>
                            <Button
                              type="button"
                              size="sm"
                              onClick={() => startVaccination(b)}
                            >
                              Bắt đầu tiêm
                            </Button>
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              onClick={() => fulfill.mutate(b.id)}
                            >
                              Đã tiêm
                            </Button>
                          </>
                        )}
                        {!terminal && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => cancel.mutate(b.id)}
                          >
                            Huỷ
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
